/**
 * 🧱 **벽에 갇힌 자리** — 지도 하나를 받아, 아이가 **설 수 없는** 오브젝트를 센다.
 *
 * ## 🔴 왜 필요한가
 * 포털·친구·링크·가게는 아이나 친구가 **그 자리에 서야** 한다. 벽 안에 놓이면
 * 화면에는 멀쩡히 보이고 에러도 없다 — 아이는 다가가려다 못 가고 그냥 지나친다.
 *
 * 2026-07-27 에 배경에서 벽을 다시 뽑았더니 능 위의 랠리 링크와 검은 고양이가 갇혔다.
 * 눈으로는 못 봤고 검사가 잡았다. 2026-09-02 에는 **게시본**에서 또 났다:
 * 마을의 `portal_event`(이벤트 광장 문)와 도서관의 `read_tree` 가 벽에 반쯤 묻혀 있었다.
 * 문은 걸어 들어가면 열리는 것인데(자동 진입 24px) 가장 가까운 열린 자리가 25px 였다.
 *
 * **그 사이 운영자에게는 아무 말도 없었다.** 검사는 저장소 지도만 보고, 아이가 보는 것은
 * 게시본이다. 그래서 판정을 여기(shared) 두고 **관리자 [✅ 출시 준비]가 같은 것을 본다.**
 *
 * ## 카드·가게는 세지 않는다
 * 카드(`card`)는 유적·좌판 **그림 위에 얹는 클릭 마커**라 그 칸이 막혀도 된다(경주존 규칙).
 * 가게(`store`)도 같다 — 굿즈 마켓 좌판은 벽에 붙은 그림이고 아이는 앞에서 눌러 연다.
 * 장식(`decor`)도 마찬가지다. 세면 잔소리가 되고, 잔소리가 되면 목록을 아무도 안 본다.
 */
import { collisionDiv, isBlockedSub } from "./collisionFine.ts";
import type { TiledMap } from "./tiledEdit.ts";

/**
 * 아이나 친구가 **그 자리에 서야** 하는 종류.
 *
 * 🔴 **가게(`store`)는 여기 없다.** 굿즈 마켓의 좌판은 벽에 붙여 놓은 그림이고 아이는
 * 그 **앞에 서서 눌러** 연다 — 여섯 좌판 모두 벽 칸에 있고 실제로 열린 기록도 있다
 * (운영 집계 2026-08). 카드와 같은 부류다. 넣으면 여섯 개가 늘 빨갛게 뜨고,
 * 늘 빨간 목록은 곧 아무도 안 본다.
 */
const MUST_STAND = new Set(["portal", "npc", "link"]);

export interface StuckObject {
  /** 오브젝트 id(속성 `id`, 없으면 이름) — **운영자에게 보여 줄 이름**. */
  id: string;
  /**
   * Tiled 오브젝트의 `name` — **화면에서 그것을 찾을 때 쓰는 열쇠**다.
   *
   * 🔴 `id` 와 같을 거라고 가정하면 안 된다. `id` 는 속성이 있으면 그걸 쓰고 없으면
   * 이름으로 떨어진다 — 속성이 있는 오브젝트에서 둘이 갈라서, 편집기가
   * **엉뚱한 것을 표시하거나 아무것도 표시 안 한다.**
   */
  name: string;
  /** `portal`·`npc`·`link`. */
  type: string;
}

/** 사람이 읽을 한 줄 — 종류마다 **겪는 일이 다르다**. */
export function stuckSay(o: StuckObject): string {
  if (o.type === "portal") return "걸어서 들어갈 수 없는 문이에요(누르면 열려요).";
  if (o.type === "npc") return "친구가 벽에 끼어 있어요.";
  return "아이가 그 자리에 설 수 없어요.";
}

/**
 * 이 지도에서 벽에 갇힌 것들. 지도 모양이 아니면 **빈 배열**이 아니라 빈 배열을 준다 —
 * 「없다」와 「못 봤다」를 가르는 것은 부르는 쪽 몫이다(못 읽은 지도를 0 으로 세지 말 것).
 */
export function stuckObjects(map: unknown): StuckObject[] {
  if (!map || typeof map !== "object") return [];
  const m = map as TiledMap & { tilewidth?: number; tileheight?: number };
  if (!Array.isArray(m.layers) || !m.width || !m.height) return [];
  const tw = m.tilewidth || 48;
  const th = m.tileheight || 48;
  const div = collisionDiv(m);
  const out: StuckObject[] = [];
  for (const layer of m.layers) {
    // 🔴 레이어가 null·objects 가 배열 아님이면 던졌다 — 관리자 [출시 준비]·check:livemaps 가 멈춘다(2026-10-11)
    const rawObjs = (layer as { objects?: unknown } | null)?.objects;
    const objs: unknown[] = Array.isArray(rawObjs) ? rawObjs : [];
    for (const raw of objs) {
      if (!raw || typeof raw !== "object") continue;
      const o = raw as {
        x?: number;
        y?: number;
        width?: number;
        height?: number;
        name?: string;
        type?: unknown;
        class?: unknown;
        properties?: { name?: string; value?: unknown }[];
      };
      // 최신 Tiled 는 class, 예전 판은 type — 우리 맵은 type 이지만 둘 다 받는다.
      // 🔴 `??` 가 아니라 `||` 다: Tiled 는 종류가 없으면 **빈 문자열**을 넣는다.
      //    `??` 로 두면 `type: ""` 에서 `class` 로 안 넘어가 **조용히 하나도 못 잡는다.**
      const type = String(o.type || o.class || "").toLowerCase();
      if (!MUST_STAND.has(type)) continue;
      if (typeof o.x !== "number" || typeof o.y !== "number") continue;
      // 웹 `mapLoader` 와 같은 기준 — 오브젝트의 **가운데**에 선다
      const px = o.x + (o.width || tw) / 2;
      const py = o.y + (o.height || th) / 2;
      if (type === "portal") {
        // 문은 가운데에 서는 게 아니라 **가까이 오면** 열린다(`WorldScene` — 아바타 가운데와의 거리 < trigger,
        // 없으면 반 칸 24px). 그래서 문은 「trigger 안에 아이가 설 수 있는 조각이 하나라도 있나」로 본다.
        // 🔴 2026-09-29: 무대 옆문(5,8)처럼 **벽 칸에 붙은 문**은 가운데가 막혀 있어도 옆 조각에 서면 열린다 —
        //    가운데만 보면 멀쩡한 문을 「갇혔다」고 늘 빨갛게 세고, 늘 빨간 목록은 아무도 안 본다.
        //    웹의 clampTrigger(12~96) 와 같은 범위로 자른다 — 과대값으로 「어디서나 열리는 문」이 통과하지 않게.
        const trigRaw = (Array.isArray(o.properties) ? o.properties : []).find((p) => p?.name === "trigger")?.value;
        const trigNum = Number(trigRaw);
        const trig = Math.max(12, Math.min(96, Number.isFinite(trigNum) && trigNum > 0 ? trigNum : tw * 0.5));
        if (enterable(m, px, py, trig)) continue;
      } else if (!isBlockedSub(m, Math.floor(px / (tw / div)), Math.floor(py / (th / div)))) continue;
      const idProp = (Array.isArray(o.properties) ? o.properties : []).find((p) => p?.name === "id");
      out.push({
        id: String(idProp?.value ?? o.name ?? "(이름없음)"),
        name: String(o.name ?? ""),
        type,
      });
    }
  }
  return out;
}

/**
 * 문 가운데에서 `trig` 안에 **아이가 설 수 있는 조각**이 하나라도 있나 — 조각 가운데로 잰다
 * (`WorldScene` 은 아바타 가운데와 문 가운데의 거리를 본다. 조각 가운데에 설 수 있으면 그 자리에 올 수 있다).
 */
function enterable(m: TiledMap & { tilewidth?: number; tileheight?: number }, px: number, py: number, trig: number): boolean {
  const tw = m.tilewidth || 48;
  const th = m.tileheight || 48;
  const div = collisionDiv(m);
  const cw = tw / div;
  const ch = th / div;
  const cols = m.width * div;
  const rows = m.height * div;
  for (let sy = Math.max(0, Math.floor((py - trig) / ch)); sy <= Math.min(rows - 1, Math.floor((py + trig) / ch)); sy++) {
    for (let sx = Math.max(0, Math.floor((px - trig) / cw)); sx <= Math.min(cols - 1, Math.floor((px + trig) / cw)); sx++) {
      const cx = (sx + 0.5) * cw;
      const cy = (sy + 0.5) * ch;
      if (Math.hypot(cx - px, cy - py) < trig && !isBlockedSub(m, sx, sy)) return true;
    }
  }
  return false;
}

/**
 * 🧹 **어디로 옮기면 되나** — 가장 가까운, 아이가 설 수 있는 칸.
 *
 * ## 🔴 왜 필요한가 (2026-09-04)
 * 편집기는 「무엇이 갇혔는지」는 알려 준다. 그런데 운영자가 할 일은 **어디로 옮길지**
 * 정하는 것이고, 벽이 넓으면 그게 제일 어렵다 — 옆 칸도 벽이면 어디까지 가야 하는지
 * 눈으로는 모른다. 실제로 마을 `portal_event` 와 도서관 `read_tree` 가 **며칠째**
 * 그대로 남아 있었다(운영자가 「한 칸씩 옮기는 건 어떻게 하는 거야?」라고 물었다).
 *
 * 그래서 코드가 찾아 준다. 판정을 여기 두는 이유는 늘 같다 — 편집기와 검사가
 * **같은 답**을 봐야 한다. 화면에서 다시 짜면 「관리자에선 되는데」가 된다.
 *
 * ## 규칙
 * · **가운데 기준**이다(`stuckObjects` 와 같은 기준 — 아이는 오브젝트 가운데에 선다)
 * · 가까운 칸부터 고리 모양으로 넓혀 가며 찾는다. 같은 거리면 **위·왼쪽 우선**이라
 *   여러 번 눌러도 답이 흔들리지 않는다(운영자가 놀란다)
 * · 지도 밖으로는 안 나간다
 * · 못 찾으면 `null` — 🔴 **아무 칸이나 지어내지 않는다.** 벽을 지워야 하는 상황이고
 *   화면은 그렇게 말해야 한다
 */
export interface FreeTile {
  /** 옮길 칸(타일 좌표). */
  tx: number;
  ty: number;
  /** 몇 칸 떨어졌나 — 화면이 「한 칸 옆」·「세 칸 옆」을 말할 수 있게. */
  steps: number;
}

/** 지도에서 이 픽셀 자리가 아이가 설 수 있는 곳인가 — `stuckObjects` 와 같은 기준. */
function standable(m: TiledMap & { tilewidth?: number; tileheight?: number }, px: number, py: number): boolean {
  const tw = m.tilewidth || 48;
  const th = m.tileheight || 48;
  const div = collisionDiv(m);
  return !isBlockedSub(m, Math.floor(px / (tw / div)), Math.floor(py / (th / div)));
}

/**
 * 갇힌 오브젝트를 옮길 칸. `max` 는 찾아볼 최대 거리(칸).
 *
 * 🔴 `max` 를 크게 두면 **지도 반대편**으로 보내 버린다 — 운영자가 「내 문이 어디 갔지」를
 * 겪는다. 여덟 칸이면 사람이 「이 근처」로 느끼는 범위다.
 */
export function nearestFreeTile(
  map: unknown,
  obj: { x?: number; y?: number; width?: number; height?: number },
  max = 8,
): FreeTile | null {
  if (!map || typeof map !== "object") return null;
  const m = map as TiledMap & { tilewidth?: number; tileheight?: number };
  if (!Array.isArray(m.layers) || !m.width || !m.height) return null;
  if (typeof obj.x !== "number" || typeof obj.y !== "number") return null;
  const tw = m.tilewidth || 48;
  const th = m.tileheight || 48;
  const w = obj.width || tw;
  const h = obj.height || th;
  // 지금 서 있는 칸 — 여기서부터 넓혀 간다
  const fromX = Math.floor((obj.x + w / 2) / tw);
  const fromY = Math.floor((obj.y + h / 2) / th);

  for (let r = 1; r <= max; r++) {
    /**
     * 🔴 **같은 거리에서 순서를 고정한다.** 집합을 돌면 브라우저·판마다 순서가 달라져
     * 같은 단추를 두 번 눌렀을 때 다른 칸으로 간다 — 운영자는 그걸 고장으로 읽는다.
     * 위(y 작은 것) → 왼쪽(x 작은 것) 순서로 늘어놓는다.
     */
    const ring: { tx: number; ty: number }[] = [];
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue; // 고리 위만
        ring.push({ tx: fromX + dx, ty: fromY + dy });
      }
    }
    ring.sort((a, b) => a.ty - b.ty || a.tx - b.tx);
    for (const t of ring) {
      if (t.tx < 0 || t.ty < 0 || t.tx >= m.width || t.ty >= m.height) continue;
      // 그 칸 **가운데**에 섰을 때 설 수 있나 — 놓는 기준과 재는 기준을 같게 둔다
      if (standable(m, t.tx * tw + tw / 2, t.ty * th + th / 2)) {
        return { tx: t.tx, ty: t.ty, steps: r };
      }
    }
  }
  return null; // 🔴 지어내지 않는다 — 벽을 지워야 하는 상황이다
}

/** 옮긴 뒤 오브젝트의 왼쪽·위 좌표(Tiled 는 그 값을 갖는다). 칸 **가운데**에 놓는다. */
export function tilePlacement(
  map: unknown,
  obj: { width?: number; height?: number },
  tile: FreeTile,
): { x: number; y: number } | null {
  if (!map || typeof map !== "object") return null;
  const m = map as TiledMap & { tilewidth?: number; tileheight?: number };
  const tw = m.tilewidth || 48;
  const th = m.tileheight || 48;
  const w = obj.width || tw;
  const h = obj.height || th;
  return { x: tile.tx * tw + tw / 2 - w / 2, y: tile.ty * th + th / 2 - h / 2 };
}

/** 운영자에게 할 말 — 몇 칸 옆으로 갔는지, 못 찾았으면 무엇을 해야 하는지. */
export function moveSay(id: string, tile: FreeTile | null): string {
  if (!tile) {
    return `「${id}」 근처에 아이가 설 수 있는 칸이 없어요 — 옆의 벽을 조금 지운 뒤 다시 눌러 주세요.`;
  }
  return `「${id}」 를 ${tile.steps}칸 옆(${tile.tx}, ${tile.ty})으로 옮겼어요. 아래 [🚀 웹에 올리기]를 눌러 주세요.`;
}
